import {
  styled,
  Kanban,
  KanbanReorderChange,
} from "@monolith-forensics/monolith-ui";
import { Task } from "../tasks.types";
import { useMemo } from "react";
import { TaskStatuses } from "../TaskStatuses";
import { TaskPriorities } from "../TaskPriorities";
import { useTaskSystemContext } from "../contexts";

export interface TaskBoardProps {}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
  overflow: hidden;
`;

const BoardArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.action.hover};
  background: ${({ theme }) =>
    theme.name === "DARK" ? "#2a2a2a" : theme.palette.background.default};
  border-radius: 5px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100px;
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const CardMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TaskBoard: React.FC<TaskBoardProps> = () => {
  const { tasks, updateTask, groupLabels, groupOrder, getCardGroup, groupBy } =
    useTaskSystemContext();

  // TODO: consider pulling from API response instead of using local state
  const timeCategories = useMemo(() => {
    return tasks
      .map((task) => task.time_category)
      .filter((timeCategory) => timeCategory !== null)
      .concat([{ id: 0, name: "No Category" }])
      .sort((a, b) => a.id - b.id);
  }, [tasks]);

  // Force Kanban remount when visible card set changes.
  // This avoids stale rendered cards when internal virtualization state lags behind filtered props.
  const boardRenderKey = useMemo(() => {
    return groupBy + tasks.map((task) => task.task_id).join("|");
  }, [tasks, groupBy]);

  const handleCardMove = (change: KanbanReorderChange<Task, string>) => {
    const { movedCard } = change;
    const movedToStatus = TaskStatuses.find(
      (status) => status.status_id.toString() === change.toGroup
    );
    const movedToPriority = TaskPriorities.find(
      (priority) => priority.priority_id.toString() === change.toGroup
    );

    const movedToTimeCategory = timeCategories?.find(
      (timeCategory) => timeCategory?.id?.toString() === change.toGroup
    );

    switch (groupBy) {
      case "status":
        updateTask(movedCard.uuid, {
          sort_value: change.newOrder,
          status: movedToStatus
            ? {
                id: movedToStatus.status_id,
                name: movedToStatus.status_name,
              }
            : undefined,
        });
        break;
      case "priority":
        updateTask(movedCard.uuid, {
          sort_value: change.newOrder,
          priority: movedToPriority
            ? {
                id: movedToPriority.priority_id,
                name: movedToPriority.priority_name,
              }
            : undefined,
        });
        break;
      case "time_category":
        updateTask(movedCard.uuid, {
          sort_value: change.newOrder,
          time_category: movedToTimeCategory
            ? {
                id: movedToTimeCategory.id,
                name: movedToTimeCategory.name,
              }
            : undefined,
        });
        break;
      default:
        break;
    }
  };

  return (
    <Wrapper>
      <BoardArea>
        <Kanban<Task, string>
          key={boardRenderKey}
          cards={tasks || []}
          groupOrder={groupOrder}
          groupLabels={groupLabels}
          columnWidth={320}
          estimatedCardHeight={100}
          overscanCount={6}
          columnGap={15}
          getCardId={(card) => card.task_id.toString()}
          getCardGroup={(card) => getCardGroup?.(card) || ""}
          getCardOrder={(card) => card.sort_value}
          renderCard={({ card }) => (
            <Card>
              <CardTitle>{card.task_name}</CardTitle>
              <CardMeta>Order: {card.sort_value}</CardMeta>
              <CardMeta>
                Status: {card.status?.name} | Priority: {card.priority?.name} |
                Time Category: {card.time_category?.name} | Due Date:{" "}
                {card.due_date}
              </CardMeta>
            </Card>
          )}
          onCardsReorder={(change) => {
            handleCardMove(change);
          }}
        />
      </BoardArea>
    </Wrapper>
  );
};
